import { ArrowDown, Activity, Layers } from "lucide-react";

export const Hero = ({ stats }) => {
  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <section
      data-testid="hero-section"
      className="relative overflow-hidden bg-grain"
    >
      {/* Aurora ambient blobs */}
      <div
        className="aurora-blob"
        style={{
          width: 480,
          height: 480,
          background: "hsl(var(--accent-green) / 0.35)",
          top: -120,
          left: -120,
        }}
      />
      <div
        className="aurora-blob"
        style={{
          width: 380,
          height: 380,
          background: "hsl(var(--accent-magenta) / 0.28)",
          top: 80,
          right: -80,
          animationDelay: "3s",
        }}
      />
      <div
        className="aurora-blob"
        style={{
          width: 300,
          height: 300,
          background: "hsl(var(--accent-cyan) / 0.25)",
          bottom: -100,
          left: "30%",
          animationDelay: "6s",
        }}
      />

      <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 lg:px-16 pt-20 pb-24 lg:pt-28 lg:pb-32">
        {/* Tag line */}
        <div
          data-testid="hero-tag"
          className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.3em] text-muted-foreground mb-8"
        >
          <span className="w-2 h-2 rounded-full bg-[hsl(var(--accent-green))] animate-pulse" />
          full-stack portal · dvassalloapps.com.ar
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-end">
          <div className="lg:col-span-9">
            <h1
              data-testid="hero-title"
              className="font-display text-5xl sm:text-6xl md:text-7xl lg:text-[8.5rem] leading-[0.85] font-black uppercase tracking-tighter text-balance"
            >
              <span className="block">Daniel</span>
              <span className="block text-aurora">Vassallo</span>
              <span className="block text-muted-foreground text-3xl sm:text-4xl md:text-5xl lg:text-6xl mt-4 font-bold">
                / apps_
              </span>
            </h1>
          </div>

          <div className="lg:col-span-3 space-y-5">
            <p className="text-base md:text-lg leading-relaxed text-muted-foreground">
              Un único hogar para todas las aplicaciones full-stack que vengo construyendo.
              Entrá, probalas, rompelas.
            </p>

            <div className="flex flex-wrap gap-3">
              <button
                data-testid="hero-cta-apps"
                onClick={() => scrollTo("apps")}
                className="group inline-flex items-center gap-2 bg-[hsl(var(--accent-green))] text-background font-mono font-bold uppercase tracking-[0.15em] text-xs px-5 py-3 border-2 border-[hsl(var(--accent-green))] hover:bg-transparent hover:text-[hsl(var(--accent-green))] transition-colors"
              >
                Explorar apps
                <ArrowDown
                  size={14}
                  className="group-hover:translate-y-0.5 transition-transform"
                />
              </button>
              <button
                data-testid="hero-cta-about"
                onClick={() => scrollTo("about")}
                className="inline-flex items-center gap-2 font-mono uppercase tracking-[0.15em] text-xs px-5 py-3 border border-border hover:border-foreground transition-colors"
              >
                Sobre mí
              </button>
            </div>
          </div>
        </div>

        {/* Stats bar */}
        <div
          data-testid="hero-stats"
          className="mt-16 lg:mt-24 grid grid-cols-2 md:grid-cols-4 border-t border-border"
        >
          <Stat
            label="apps online"
            value={stats?.total_apps ?? "—"}
            icon={<Layers size={14} />}
            testId="stat-apps"
          />
          <Stat
            label="clicks totales"
            value={stats?.total_clicks ?? 0}
            icon={<Activity size={14} />}
            testId="stat-clicks"
          />
          <Stat
            label="categorías"
            value={stats?.categories?.length ?? "—"}
            testId="stat-categories"
          />
          <Stat
            label="status"
            value={
              <span className="text-[hsl(var(--accent-green))]">live</span>
            }
            testId="stat-status"
          />
        </div>
      </div>
    </section>
  );
};

const Stat = ({ label, value, icon, testId }) => (
  <div
    data-testid={testId}
    className="border-r last:border-r-0 border-b md:border-b-0 border-border py-5 px-4 md:px-6 flex flex-col gap-1.5"
  >
    <div className="flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
      {icon}
      {label}
    </div>
    <div className="font-display text-2xl md:text-3xl font-bold tabular-nums">
      {value}
    </div>
  </div>
);

export default Hero;
